import React, { useState, useEffect, useRef, useContext } from "react";

import { useNavigate, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import { Eye, Loader2, Star, Users, Check, CheckCheck, Clock } from "lucide-react";

import { AvatarImage, AvatarFallback, AvatarPreview } from "../ui/avatar";
import { canalDoTicket } from "../../helpers/canalDoTicket.jsx";
import { esperaDoTicket } from "../../helpers/tempoDeEspera";
import { rotuloPresenca } from "../../hooks/usePresence";
import TicketPeekDialog from "../TicketPeekDialog";
import TransferTicketModal from "../TransferTicketModal";
import ResolverTicketModal from "../ResolverTicketModal";
import { getPublicFileUrl } from "../../config";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

// Selo sólido e curto, como no fservice: fundo colorido, texto branco em
// caixa alta e 9px. Reconhece-se pela cor antes de ser lido.
const Selo = ({ children, style, className, title }) => (
  <span
    style={style}
    title={title}
    className={cn(
      "max-w-36 truncate rounded-[3px] px-1 py-[2px] text-[9px] font-bold uppercase leading-none text-white",
      className,
    )}
  >
    {children}
  </span>
);

// Ação da linha: texto pequeno em azul, sem moldura — o mesmo tratamento da
// referência, onde as ações ficam discretas até a pessoa precisar delas.
const Acao = ({ children, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className="rounded px-1.5 py-0.5 text-[11px] font-medium text-[#0872B9] transition-colors hover:bg-[#0872B9]/10 disabled:opacity-40 dark:text-sky-300 dark:hover:bg-sky-300/10"
  >
    {children}
  </button>
);

const cortar = (texto, tamanho) => {
  const t = String(texto || "");

  return t.length > tamanho ? `${t.slice(0, tamanho)}...` : t;
};

// A última mensagem chega crua: localização vem como imagem em base64 e
// contato como VCARD. Mostrar isso na lista não diz nada a quem lê.
const previaDaMensagem = (lastMessage) => {
  const texto = String(lastMessage || "");

  if (texto.includes("data:image/png;base64")) return "Localização";
  if (texto.includes("BEGIN:VCARD")) return "Contato";

  return cortar(texto, 40);
};

const TicketListItem = ({ ticket, presenca, mostrarConexao, isGroup, slaCountGroupsMap, loadingWhatsapps }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [transferindo, setTransferindo] = useState(false);
  // Encerrar deixou de ser um clique só: o modal pede o motivo e pergunta se
  // o cliente confirma antes.
  const [resolvendo, setResolvendo] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  // REGRA (Solicitacao do usuario): Na ABA DE GRUPOS (isGroup === "true")
  // se a conexao (whatsapp) que o ticket veio estiver com "SLA de grupos"
  // DESMARCADO (slaCountGroups === false), entao OS BOTOES Aceitar,
  // Transferir e Ignorar NAO PODEM aparecer. Os demais botoes: Resolver,
  // Reabrir, Favorito permanecem. Se for QUALQUER OUTRA ABA (caixa de
  // entrada, abertos, pendentes, minhas, fechados, chat interno, busca)
  // a regra NAO se aplica — todos os botoes continuam aparecendo como
  // antes.
  //
  // FLASH FIX: Para nao "aparecer rapidinho e sumir" ao clicar / trocar
  // de ticket: se estamos NA ABA GRUPOS e ainda estamos CARREGANDO as
  // conexoes (loadingWhatsapps=true) OU temos o id da conexao mas ela
  // NAO foi carregada no mapa AINDA → esconde temporariamente as acoes.
  // Assim o usuario NUNCA ve os botoes "piscar" quando troca de aba ou
  // abre um ticket novo.
  const estaNaAbaGrupos = String(isGroup || "") === "true";
  const idConexao = ticket?.whatsappId ?? ticket?.whatsapp?.id;
  const numeroConexao =
    idConexao === null || idConexao === undefined ? NaN : Number(idConexao);
  const mapaValido = slaCountGroupsMap && slaCountGroupsMap instanceof Map;
  const conexaoTemSlaGrupos =
    mapaValido && Number.isFinite(numeroConexao)
      ? slaCountGroupsMap.get(numeroConexao)
      : undefined;
  const conexaoNaoEncontradaAinda =
    estaNaAbaGrupos && Number.isFinite(numeroConexao) && conexaoTemSlaGrupos === undefined;
  const esconderAcoesDeFilaGrupo =
    estaNaAbaGrupos &&
    (loadingWhatsapps === true ||
      conexaoTemSlaGrupos === false ||
      conexaoNaoEncontradaAinda);

  // Favorito é marcação pessoal: o estado local responde ao clique na hora e
  // só volta atrás se o servidor recusar.
  const [favorito, setFavorito] = useState(Boolean(ticket.favoritado));

  useEffect(() => {
    // O ticket que chega pelo socket é o mesmo para todo mundo e não carrega
    // "favoritado"; sem esta guarda, uma mensagem nova apagaria a estrela.
    if (ticket.favoritado !== undefined) {
      queueMicrotask(() => setFavorito(Boolean(ticket.favoritado)));
    }
  }, [ticket.favoritado]);

  const alternarFavorito = async (e) => {
    e.stopPropagation();

    const novo = !favorito;
    setFavorito(novo);

    try {
      if (novo) await api.post(`/ticket-favorites/${ticket.id}`);
      else await api.delete(`/ticket-favorites/${ticket.id}`);
    } catch (err) {
      setFavorito(!novo);
      toastError(err);
    }
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const mudarStatus = async (status, userId, irParaOTicket) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { status, userId });
      if (irParaOTicket) navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    if (isMounted.current) setLoading(false);
  };

  const isSelected = ticketId && +ticketId === ticket.id;
  const naoLidas = Number(ticket.unreadMessages) || 0;

  const canal = canalDoTicket(ticket);
  const CanalIcone = canal.icone;
  const espera = esperaDoTicket(ticket);

  // Contato que chegou só com o identificador novo do WhatsApp não tem nome
  // nem telefone; sem isto a linha do atendimento aparece em branco e não dá
  // para saber de quem é a conversa.
  //
  // Quando há apenas o LID, mostramos os últimos 6 dígitos para ser identificável
  // (melhor que um texto genérico que deixa todas as conversas parecidas).
  const ultimosDigitosLid = (lid) => {
    if (!lid) return "";
    const nucleo = String(lid).split("@")[0].split(":")[0];
    return nucleo.slice(-6);
  };

  const nomeExibido =
    ticket.contact?.name && ticket.contact?.name !== "Contato sem número"
      ? ticket.contact.name
      : ticket.contact?.number
      ? ticket.contact.number
      : ticket.contact?.lid
      ? `Contato novo · ${ultimosDigitosLid(ticket.contact.lid)}`
      : "Contato sem número";

  const initials = (nomeExibido || "?")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <>
      <TransferTicketModal
        modalOpen={transferindo}
        onClose={() => setTransferindo(false)}
        ticketid={ticket.id}
        ticketWhatsappId={ticket.whatsappId}
      />

      <ResolverTicketModal
        open={resolvendo}
        onClose={() => setResolvendo(false)}
        ticket={ticket}
      />

      <div
        role="button"
        tabIndex={0}
        className={cn(
          "relative flex cursor-pointer gap-3 border-b px-3 py-2.5 transition-colors hover:bg-accent/60",
          "focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
          isSelected && "bg-accent",
        )}
        onClick={() => navigate(`/tickets/${ticket.id}`)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            navigate(`/tickets/${ticket.id}`);
          }
        }}
      >
        {/* Faixa da fila na borda, como na referência. */}
        <span
          className="absolute inset-y-0 left-0 w-1"
          style={{ backgroundColor: ticket.queue?.color || "transparent" }}
        />

        <AvatarPreview
          className="h-[50px] w-[50px] shrink-0 self-start"
          previewUrl={ticket?.contact?.profilePicUrl}
          alt={nomeExibido}
        >
          <AvatarImage
            src={getPublicFileUrl(ticket?.contact?.profilePicUrl)}
            alt={nomeExibido}
          />
          <AvatarFallback>{initials}</AvatarFallback>
        </AvatarPreview>

        <div className="min-w-0 flex-1">
          {/* Nome, com o ícone do canal antes e o "espiar" depois. */}
          <div className="flex min-w-0 items-center gap-1.5 pr-14">
            {ticket.isGroup && (
              <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            )}

            <span
              title={canal.rotulo}
              aria-label={`Canal: ${canal.rotulo}`}
              className={cn(
                "flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                canal.classe,
              )}
            >
              <CanalIcone className="h-2.5 w-2.5" />
            </span>

            <span
              className={cn(
                "min-w-0 truncate text-sm",
                naoLidas > 0 ? "font-bold" : "font-medium",
              )}
            >
              {cortar(nomeExibido, 60)}
            </span>

            <TicketPeekDialog ticket={ticket} />
          </div>

          <p
            className={cn(
              "mt-0.5 flex items-center truncate pr-14 gap-1 text-xs",
              naoLidas > 0
                ? "font-bold text-foreground"
                : "text-muted-foreground",
            )}
          >
            {/* Ícone do visto na sidebar: mostra SÓ quando a última mensagem
                foi enviada POR NÓS (fromMe = true). Ticket.fromMe marca quem
                disparou a última mensagem e está nas attributes que o backend
                emite quando é a última mensagem. */}
            {Boolean(ticket.fromMe) && (() => {
              let ack = NaN;
              if (ticket.lastMessageAck !== undefined && ticket.lastMessageAck !== null) {
                ack = Number(ticket.lastMessageAck);
              } else if (typeof ticket.lastAck !== "undefined" && ticket.lastAck !== null) {
                ack = Number(ticket.lastAck);
              }
              if (!Number.isFinite(ack)) return null;
              if (ack <= 0) return <Clock className="h-3 w-3 shrink-0" />;
              if (ack === 1) return <Check className="h-3 w-3 shrink-0" />;
              if (ack === 2) return <CheckCheck className="h-3 w-3 shrink-0" />;
              return (
                <CheckCheck className="h-3 w-3 shrink-0 text-emerald-500" />
              );
            })()}
            {/* Digitando vence a última mensagem: é o que acontece agora. */}
            {rotuloPresenca(presenca) ? (
              <span className="font-medium text-emerald-600 dark:text-emerald-400 truncate">
                {rotuloPresenca(presenca)}
              </span>
            ) : ticket.lastMessage ? (
              <MarkdownWrapper className="truncate min-w-0">
                {previaDaMensagem(ticket.lastMessage)}
              </MarkdownWrapper>
            ) : (
              <>&nbsp;</>
            )}
          </p>

          {/* Canal, conexão, fila e atendente. O canal por extenso porque o
              selo do avatar tem 4px: com WhatsApp, Instagram, Telegram e
              Messenger na mesma lista, é ele que diz onde a conversa está. */}
          <div className="mt-1 flex flex-wrap items-center gap-[3px]">
            <Selo style={{ backgroundColor: canal.corSelo }}>
              {canal.rotulo}
            </Selo>

            {/* Neutro: a cor da marca agora identifica o canal, e repeti-la
                aqui faria os dois selos virarem um borrão só. */}
            {ticket.whatsapp?.name && (
              <Selo style={{ backgroundColor: "#525252" }}>
                {ticket.whatsapp.name}
              </Selo>
            )}

            {/* VERBATIM usuário 25/09/2026: "Se não tem fila cadastrada, não precisa mostrar SEM FILA — 
                 sem quebrar nada do sistema e se tiver fila aí mostra."
                 Condição: exibe o selo SÓ quando há fila EXISTENTE (ticket.queue é um objeto preenchido com id + name).
                 Antes: sempre exibia, fallback = "Sem fila" cinza, poluía visual (3 selos por ticket: WHATSAPP · PERSON · SEM FILA).
                 Depois: SE fila → exibe cor + nome. SEM fila → NÃO exibe NADA (some o selo por completo).
                 NÃO toca em nada mais da linha (canal, conexão, atendente, tags, ações). */}
            {ticket.queue &&
              ticket.queue.id !== undefined &&
              ticket.queue.id !== null &&
              String(ticket.queue.id).length > 0 &&
              ticket.queue.name &&
              String(ticket.queue.name).trim().length > 0 && (
                <Selo style={{ backgroundColor: ticket.queue.color || "#7c7c7c" }}>
                  {ticket.queue.name}
                </Selo>
              )}

            {/* De quem é a conversa hoje. Na aba "Do setor" aparece conversa
                de colega e qualquer um pode puxá-la — o rótulo explícito é o
                que evita assumir sem perceber que já tem atendente. */}
            {ticket.user?.name && (
              <Selo
                title={`Atribuído a ${ticket.user.name}`}
                style={{
                  backgroundColor:
                    ticket.userId === user?.id ? "#000000" : "#b45309",
                }}
              >
                {ticket.userId === user?.id
                  ? ticket.user.name
                  : `Atribuído a ${ticket.user.name}`}
              </Selo>
            )}

            {/* Quando o usuário foi mencionado numa nota interna e ainda não
                abriu (unreadMentions > 0), mostra selo destacado "MENSAGEM
                PRIVADA" para a pessoa identificar rápido na fila. */}
            {Number(ticket.unreadMentions) > 0 && (
              <Selo
                className="animate-pulse"
                style={{ backgroundColor: "#d97706" }}
                title={`${ticket.unreadMentions} menção(ões) direta em nota interna — responda com mensagem privada`}
              >
                <Eye className="mr-1 inline h-2 w-2 align-[-2px]" />
                Mensagem privada
              </Selo>
            )}

            {/* Este atendimento tem mensagem agendada pendente — criada por
                dentro dele (ticketId) ou pela tela de Agendamentos com o mesmo
                contato. O campo é calculado pela API /tickets; a data e a hora
                do próximo envio ficam no tooltip. */}
            {ticket.agendamentoPendente && (() => {
              const quando = parseISO(ticket.agendamentoPendente);
              const valido = !Number.isNaN(quando.getTime());
              return (
                <Selo
                  style={{ backgroundColor: "#7c3aed" }}
                  title={
                    valido
                      ? `Conversa agendada para ${format(quando, "dd/MM/yyyy")} às ${format(quando, "HH:mm")}`
                      : "Este atendimento tem conversa agendada"
                  }
                >
                  <Clock className="mr-1 inline h-2 w-2 align-[-2px]" />
                  Agendado
                </Selo>
              );
            })()}
          </div>

          {ticket.tags?.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-[3px]">
              {ticket.tags.map((tag) => (
                <Selo key={tag.id} style={{ backgroundColor: tag.color }}>
                  {tag.name}
                </Selo>
              ))}
            </div>
          )}

          {/* Ações da linha, alinhadas à direita como na referência. */}
          <div className="mt-1 flex items-center justify-end gap-0.5">
            {espera && (
              <span
                className={cn(
                  "mr-auto text-[11px] font-semibold tabular-nums",
                  espera.classe,
                )}
                title={
                  ticket.status === "pending"
                    ? "Esperando há"
                    : "Sem resposta há"
                }
              >
                {espera.rotulo}
              </span>
            )}

            {loading && (
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            )}

            {!esconderAcoesDeFilaGrupo && ticket.status === "pending" && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("open", user?.id, true)}
              >
                {i18n.t("ticketsList.buttons.accept")}
              </Acao>
            )}

            {!esconderAcoesDeFilaGrupo && ticket.status !== "closed" && (
              <Acao disabled={loading} onClick={() => setTransferindo(true)}>
                Transferir
              </Acao>
            )}

            {ticket.status === "open" && (
              <Acao disabled={loading} onClick={() => setResolvendo(true)}>
                Resolver
              </Acao>
            )}

            {!esconderAcoesDeFilaGrupo && ticket.status === "pending" && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("closed", null)}
              >
                Ignorar
              </Acao>
            )}

            {ticket.status === "closed" && (
              <Acao
                disabled={loading}
                onClick={() => mudarStatus("open", user?.id, true)}
              >
                Reabrir
              </Acao>
            )}

            {/* Estrela: marcação só de quem está logado. Área de toque de
                28px para funcionar no celular sem abrir a conversa por engano. */}
            <button
              type="button"
              aria-label={
                favorito ? "Remover dos favoritos" : "Marcar como favorita"
              }
              aria-pressed={favorito}
              title={
                favorito ? "Remover dos favoritos" : "Marcar como favorita"
              }
              onClick={alternarFavorito}
              className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors",
                favorito
                  ? "text-amber-500 hover:bg-amber-500/10"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Star
                className="h-4 w-4"
                fill={favorito ? "currentColor" : "none"}
              />
            </button>
          </div>
        </div>

        {/* Horário e contador ficam no canto superior direito, fora do fluxo,
            para não empurrar o nome nem as ações. */}
        <div className="absolute right-3 top-2.5 flex flex-col items-end gap-1">
          <span
            className={cn(
              "text-[11px] tabular-nums",
              naoLidas > 0
                ? "font-bold text-emerald-600 dark:text-emerald-400"
                : "text-muted-foreground",
            )}
          >
            {isSameDay(parseISO(ticket.updatedAt), new Date())
              ? format(parseISO(ticket.updatedAt), "HH:mm")
              : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
          </span>

          {naoLidas > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-bold tabular-nums text-white">
              {naoLidas}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

export default TicketListItem;
